import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../../components/Layout/Navbar";
import TodoCard from "../../components/TodoCard/TodoCard";
import axios from "axios";
import toast from "react-hot-toast";
import "./HomePage.css";

const HomePage = () => {
  const [todos, setTodos] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [editTodo, setEditTodo] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // all, completed, pending
  const [isLoading, setIsLoading] = useState(true);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("todoapp")) || null;
    } catch (e) {
      return null;
    }
  })();
  const token = user?.token;

  const fetchTodos = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/todo/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTodos(res.data.todos || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load tasks. Please login again.");
    } finally {
      setIsLoading(false);
    }
  }, [token, BASE_URL]);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  // Create Task
  const handleCreateTodo = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return toast.error("All fields required!");

    try {
      const res = await axios.post(
        `${BASE_URL}/todo/create`,
        { title, description, isCompleted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.msg);
      closeModal();
      setTodos(prev => [res.data.todo, ...prev]);
    } catch (error) {
      console.error(error);
      toast.error("Error creating task");
    }
  };

  // Update Task
  const handleUpdateTodo = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return toast.error("All fields required!");

    try {
      const res = await axios.put(
        `${BASE_URL}/todo/${editTodo._id}`,
        { title, description, isCompleted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.msg);
      setTodos(prev => prev.map(t => (t._id === editTodo._id ? res.data.todo : t)));
      closeModal();
    } catch (error) {
      console.error(error);
      toast.error("Update failed");
    }
  };

  const handleEditClick = (todo) => {
    setEditTodo(todo);
    setTitle(todo.title);
    setDescription(todo.description);
    setIsCompleted(todo.isCompleted);
    setShowModal(true);
  };

  const deleteTodo = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/todo/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTodos(prev => prev.filter(t => t._id !== id));
      toast.success("Task deleted");
    } catch (error) {
      console.error(error);
      toast.error("Delete failed");
    }
  };

  const handleToggleStatus = async (todo) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/todo/${todo._id}`,
        { isCompleted: !todo.isCompleted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTodos(prev => prev.map(t => (t._id === todo._id ? res.data.todo : t)));
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    }
  };

  const openModal = () => {
    setShowModal(true);
    setEditTodo(null);
    setTitle("");
    setDescription("");
    setIsCompleted(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditTodo(null);
    setTitle("");
    setDescription("");
    setIsCompleted(false);
  };

  const filteredTodos = todos
    .filter(t => t.title.toLowerCase().includes(search.toLowerCase()))
    .filter(t => {
      if (filterStatus === "completed") return t.isCompleted;
      if (filterStatus === "pending") return !t.isCompleted;
      return true;
    });

  const stats = {
    total: todos.length,
    completed: todos.filter(t => t.isCompleted).length,
    pending: todos.filter(t => !t.isCompleted).length,
  };

  return (
    <>
      <Navbar />
      <div className="container">
        {/* Header Section with Stats */}
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">
              <span className="title-icons">📋</span>
              Your Tasks
            </h1>
            <div className="stats-container">
              <div className="stat-card">
                <span className="stat-number">{stats.total}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat-card stat-pending">
                <span className="stat-number">{stats.pending}</span>
                <span className="stat-label">Pending</span>
              </div>
              <div className="stat-card stat-completed">
                <span className="stat-number">{stats.completed}</span>
                <span className="stat-label">Completed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="controls-section">
          <div className="search-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="search"
              placeholder="Search your tasks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterStatus === "all" ? "active" : ""}`}
              onClick={() => setFilterStatus("all")}
            >
              All
            </button>
            <button
              className={`filter-btn ${filterStatus === "pending" ? "active" : ""}`}
              onClick={() => setFilterStatus("pending")}
            >
              Pending
            </button>
            <button
              className={`filter-btn ${filterStatus === "completed" ? "active" : ""}`}
              onClick={() => setFilterStatus("completed")}
            >
              Completed
            </button>
          </div>

          <button className="btn btn-primary create-btn" onClick={openModal}>
            <span className="btn-icon">+</span>
            Create Task
          </button>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editTodo ? "✏️ Edit Task" : "✨ Create New Task"}</h3>
                <button className="close-btn" onClick={closeModal}>×</button>
              </div>
              <form onSubmit={editTodo ? handleUpdateTodo : handleCreateTodo}>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    placeholder="Enter task title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Enter task description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="4"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isCompleted}
                      onChange={(e) => setIsCompleted(e.target.checked)}
                      className="checkbox-input"
                    />
                    <span className="checkbox-custom"></span>
                    Mark as completed
                  </label>
                </div>
                <div className="modal-buttons">
                  <button type="submit" className="btn btn-success">
                    {editTodo ? "💾 Update Task" : "✅ Save Task"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Todo List */}
        <div className="todo-list">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading tasks...</p>
            </div>
          ) : filteredTodos.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>No tasks found</h3>
              <p>
                {search
                  ? "Try adjusting your search"
                  : "Create your first task to get started!"}
              </p>
            </div>
          ) : (
            filteredTodos.map((todo, index) => (
              <div
                key={todo._id}
                className="todo-card-wrapper"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <TodoCard
                  todo={todo}
                  onDelete={deleteTodo}
                  onEdit={handleEditClick}
                  onToggleStatus={handleToggleStatus}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default HomePage;